import { TextFieldProps } from '@mui/material';
import { useCallback, useMemo } from 'react';
import useMaxValue from '../../../modules/orders/products/hooks/useMaxValue';
import { getAvailabilityColor } from '../../../modules/orders/products/orderProducts.utils';
import { ProductGridInputProps } from '../types/productGridInput.types';

const DIGIT_PATTERN = /\d/;

const isBlockedKey = ({ key, ctrlKey, metaKey, altKey }: React.KeyboardEvent<HTMLDivElement>) =>
  key.length === 1 && !DIGIT_PATTERN.test(key) && !ctrlKey && !metaKey && !altKey;

const useProductGridInput = ({
  maxValue,
  gridItem,
  disabled,
  inputProps,
  textFieldProps,
  productColorAvailability,
  onEnter,
  onChangeGrid,
}: ProductGridInputProps) => {
  const defaultValue = gridItem.quantity || '';
  const { handleChangeValue } = useMaxValue();

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChangeValue(event, maxValue ?? undefined, true);
    },
    [handleChangeValue, maxValue],
  );

  const textFieldInputProps: TextFieldProps['inputProps'] = useMemo(
    () => ({
      min: 0,
      max: maxValue && maxValue + 1,
      inputMode: 'numeric',
      ...inputProps,
    }),
    [maxValue, inputProps],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => event.target.select(),
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isBlockedKey(event)) {
        event.preventDefault();
        return;
      }

      if (textFieldProps?.onKeyDown) {
        textFieldProps.onKeyDown(event);
        return;
      }

      if (event.key === 'Enter') {
        onEnter?.();
      }
    },
    [onEnter, textFieldProps?.onKeyDown],
  );

  const textInputProps: TextFieldProps = useMemo(
    () => ({
      color: getAvailabilityColor(productColorAvailability),
      defaultValue,
      type: 'number',
      inputProps: textFieldInputProps,
      disabled,
      onInput: handleInput,
      onFocus: handleFocus,
      onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        onChangeGrid?.({
          value: +event.target.value,
          gridItem,
          availability: productColorAvailability,
        }),
      ...textFieldProps,
      onKeyDown: handleKeyDown,
    }),
    [
      gridItem,
      disabled,
      handleInput,
      onChangeGrid,
      handleFocus,
      defaultValue,
      textFieldInputProps,
      handleKeyDown,
      textFieldProps,
      productColorAvailability,
    ],
  );

  return {
    textInputProps,
  };
};

export default useProductGridInput;
